import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, CheckCircle2, Clock, XCircle, Edit3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BuyerMembershipStatus
 * Consolidated status banner shown at the top of the Buyer Dashboard. Reads
 * `/api/buyer/membership-status` and renders one of five states:
 *   - active                          → hidden (empty)
 *   - pending_payment_verification    → yellow "awaiting admin verification"
 *   - payment_rejected                → red "payment rejected · edit & resubmit"
 *   - clarification_requested         → orange "admin has requested clarification"
 *   - not_activated                   → gold "activate your membership"
 *
 * On rejection / clarification, exposes an inline mini-form to correct
 * payment details and POST /api/buyer/manual-payment/resubmit.
 */
export default function BuyerMembershipStatus({ onStatusChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({ method: '', bank_name: '', reference_number: '', transaction_date: '', amount: '', remarks: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/buyer/membership-status`, { withCredentials: true });
      setData(d);
      onStatusChange && onStatusChange(d);
    } catch (e) {
      // silent — non-critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const mp = data?.latest_manual_payment;
  const status = data?.membership_status || 'not_activated';

  const startEdit = () => {
    setEdit({
      method: mp?.method || '',
      bank_name: mp?.bank_name || '',
      reference_number: mp?.reference_number || '',
      transaction_date: mp?.transaction_date || '',
      amount: mp?.amount ?? '',
      remarks: mp?.remarks || '',
    });
    setEditing(true);
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (edit.method) payload.method = edit.method;
      if (edit.bank_name) payload.bank_name = edit.bank_name;
      if (edit.reference_number) payload.reference_number = edit.reference_number;
      if (edit.transaction_date) payload.transaction_date = edit.transaction_date;
      if (edit.amount !== '' && edit.amount !== null) payload.amount = Number(edit.amount);
      if (edit.remarks !== undefined) payload.remarks = edit.remarks;
      await axios.post(`${API}/buyer/manual-payment/resubmit`, payload, { withCredentials: true });
      toast.success('Payment details resubmitted. Awaiting admin verification.');
      setEditing(false);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to resubmit payment details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (status === 'active') return null; // handled by existing founding/subscription banner in BuyerDashboard

  // Common banner shell
  const shell = (tone, icon, title, body) => (
    <div className={`mb-6 p-4 rounded-lg border-2 ${tone}`} data-testid={`membership-status-banner-${status}`}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5">{icon}</div>
        <div className="flex-1">
          <div className="font-bold text-navy mb-1">{title}</div>
          <div className="text-xs text-muted-foreground leading-relaxed">{body}</div>
        </div>
      </div>
    </div>
  );

  if (status === 'pending_payment_verification' && mp) {
    return shell(
      'bg-yellow-50 border-yellow-300',
      <Clock className="h-5 w-5 text-yellow-600" />,
      'Payment Pending Verification',
      <>
        Your registration has been submitted successfully. Your <strong>{mp.method?.toUpperCase()}</strong> payment (reference <strong>{mp.reference_number}</strong>, ₹{Number(mp.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}) is awaiting verification by the ProcureOne Ultra Admin Team. You&apos;ll receive an email once verified — typically 3–5 working days.
      </>
    );
  }

  if ((status === 'payment_rejected' || status === 'clarification_requested') && mp) {
    const isReject = status === 'payment_rejected';
    return (
      <div className={`mb-6 p-4 rounded-lg border-2 ${isReject ? 'bg-red-50 border-red-300' : 'bg-orange/10 border-orange/40'}`} data-testid={`membership-status-banner-${status}`}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{isReject ? <XCircle className="h-5 w-5 text-red-600" /> : <AlertCircle className="h-5 w-5 text-orange" />}</div>
          <div className="flex-1">
            <div className="font-bold text-navy mb-1">{isReject ? 'Payment Rejected' : 'Clarification Requested'}</div>
            <div className="text-xs text-muted-foreground leading-relaxed mb-2">
              {isReject
                ? <>Your <strong>{mp.method?.toUpperCase()}</strong> payment could not be verified. <strong>Reason:</strong> {mp.rejection_reason || 'No reason provided.'} — please correct the details below and resubmit. You do <strong>not</strong> need to fill the registration form again.</>
                : <>The Admin team has requested clarification on your payment. <strong>Note:</strong> {mp.clarification_message || 'Please review and resubmit.'}</>}
            </div>
            {!editing ? (
              <button onClick={startEdit} className="inline-flex items-center px-3 py-1.5 rounded bg-navy text-white text-xs font-semibold" data-testid="manual-resubmit-edit-btn">
                <Edit3 className="h-3.5 w-3.5 mr-1.5" /> Edit &amp; Resubmit Payment Details
              </button>
            ) : (
              <div className="mt-3 grid gap-2 bg-white p-3 rounded border border-border" data-testid="manual-resubmit-form">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground">Method</label>
                    <select value={edit.method} onChange={e => setEdit({ ...edit, method: e.target.value })} className="w-full px-2 py-1.5 rounded border border-input text-sm" data-testid="resubmit-method">
                      <option value="cheque">Cheque</option>
                      <option value="neft">NEFT</option>
                      <option value="rtgs">RTGS</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground">Bank Name</label>
                    <input value={edit.bank_name} onChange={e => setEdit({ ...edit, bank_name: e.target.value })} className="w-full px-2 py-1.5 rounded border border-input text-sm" data-testid="resubmit-bank" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground">{edit.method === 'cheque' ? 'Cheque Number' : 'UTR / Reference'}</label>
                    <input value={edit.reference_number} onChange={e => setEdit({ ...edit, reference_number: e.target.value })} className="w-full px-2 py-1.5 rounded border border-input text-sm" data-testid="resubmit-reference" />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground">{edit.method === 'cheque' ? 'Cheque Date' : 'Transaction Date'}</label>
                    <input type="date" value={edit.transaction_date} onChange={e => setEdit({ ...edit, transaction_date: e.target.value })} className="w-full px-2 py-1.5 rounded border border-input text-sm" data-testid="resubmit-date" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground">Amount (₹)</label>
                  <input type="number" step="0.01" value={edit.amount} onChange={e => setEdit({ ...edit, amount: e.target.value })} className="w-full px-2 py-1.5 rounded border border-input text-sm" data-testid="resubmit-amount" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground">Remarks (optional)</label>
                  <textarea value={edit.remarks} onChange={e => setEdit({ ...edit, remarks: e.target.value })} rows={2} className="w-full px-2 py-1.5 rounded border border-input text-sm" data-testid="resubmit-remarks" />
                </div>
                <div className="flex gap-2">
                  <button onClick={submitEdit} disabled={saving} className="px-3 py-1.5 rounded bg-orange text-white text-xs font-bold disabled:opacity-50 inline-flex items-center" data-testid="resubmit-submit-btn">
                    {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Submitting…</> : 'Submit Updated Details'}
                  </button>
                  <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded border border-border text-xs" data-testid="resubmit-cancel-btn">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'not_activated') {
    return shell(
      'bg-gold/10 border-gold/40',
      <CheckCircle2 className="h-5 w-5 text-gold" />,
      'Activate your Buyer Membership',
      <>Head to Checkout to activate your yearly subscription via Razorpay, Cheque, NEFT or RTGS.</>
    );
  }

  return null;
}
